import { useMemo } from "react";
import { CheckCircle2, Plus, Settings2, ShieldCheck, Sparkles, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

type Rule = {
  enabled?: boolean;
  type?: "fixed" | "percent";
  value?: number;
  unit_minutes?: number;
  [key: string]: unknown;
};

type PackageValidityUnit = "day" | "week" | "month";

type PackageOption = {
  id?: string;
  name: string;
  units: number;
  price: number;
  validity_quantity: number;
  validity_unit: PackageValidityUnit;
  variant_key?: "banho" | "tosa" | "banho_e_tosa" | null;
};

type PricingConfig = {
  packages?: PackageOption[];
  recurrence?: { enabled?: boolean; discount_percent?: number };
  long_stay_discounts?: Array<{ min_units: number; discount_percent: number }>;
  extras?: Array<{
    id: string;
    code: string;
    name: string;
    enabled: boolean;
    type: "fixed" | "percent";
    value: number;
  }>;
  distance?: { enabled?: boolean; included_km?: number; price_per_km?: number; minimum_fare?: number };
  waiting?: { enabled?: boolean; free_minutes?: number; price_per_minute?: number };
  extra_stop?: { enabled?: boolean; value?: number };
  round_trip?: { enabled?: boolean; value?: number };
  [key: string]: unknown;
};

type PricingPolicyFees = {
  marketplace_first?: number;
  marketplace_second?: number;
  marketplace_recurring?: number;
  host_originated?: number;
  package_or_subscription?: number;
  maximum_effective_percent?: number;
  maximum_amount?: number;
};

export type GuidedPricingService = {
  id: number;
  tipo_servico_id: number;
  nome: string;
  preco_base: number;
  preco_banho?: number | null;
  preco_tosa?: number | null;
  preco_banho_tosa?: number | null;
  pricing_unit: string;
  alternative_units: string[];
  allowed_modifiers: string[];
  pricing_config: PricingConfig;
  client_origin_default?: "marketplace" | "host";
  pricing_policy_fees?: PricingPolicyFees;
};

type Props = {
  service: GuidedPricingService;
  saving: boolean;
  onPatchService: (patch: Partial<GuidedPricingService>) => void;
  onPatchConfig: (key: string, patch: Record<string, unknown>) => void;
  onSave: () => void;
  onShowAdvanced: () => void;
};

type BathGroomingPriceKey = "preco_banho" | "preco_tosa" | "preco_banho_tosa";
const BATH_GROOMING_OPTIONS: Array<{ key: BathGroomingPriceKey; label: string; help: string }> = [
  { key: "preco_banho", label: "Só banho", help: "Higiene e secagem" },
  { key: "preco_tosa", label: "Só tosa", help: "Tosa sem banho" },
  { key: "preco_banho_tosa", label: "Banho e tosa", help: "Serviço completo" },
];

const SIMPLE_QUESTIONS: Array<{
  key: string;
  label: string;
  question: string;
  codes: string[];
}> = [
  { key: "extra_pet", label: "Pet adicional", question: "Você cobra por pet adicional?", codes: ["PET_ADICIONAL"] },
  { key: "holiday", label: "Feriado", question: "Você cobra um valor diferente em feriados?", codes: ["FERIADO"] },
  { key: "weekend", label: "Fim de semana", question: "Você cobra um valor diferente aos finais de semana?", codes: ["FIM_DE_SEMANA"] },
  { key: "night", label: "Horário noturno", question: "Você cobra adicional para atendimento noturno?", codes: ["NOTURNO"] },
  { key: "puppy", label: "Filhote", question: "Você cobra adicional para cuidar de filhotes?", codes: ["FILHOTE"] },
  { key: "special_care", label: "Cuidados especiais", question: "Você cobra por cuidados especiais?", codes: ["CUIDADO_ESPECIAL"] },
  { key: "medication", label: "Medicação", question: "Você cobra para administrar medicamentos?", codes: ["MEDICACAO"] },
  { key: "transport", label: "Busca ou deslocamento", question: "Você cobra para buscar, entregar ou se deslocar?", codes: ["BUSCA_ENTREGA", "DESLOCAMENTO"] },
  { key: "extra_duration", label: "Tempo adicional", question: "Você cobra quando o serviço ultrapassa o tempo combinado?", codes: ["DURACAO_EXTRA"] },
];

const CORE_CODES = new Set([
  "PET_ADICIONAL", "FERIADO", "FIM_DE_SEMANA", "NOTURNO", "FILHOTE",
  "CUIDADO_ESPECIAL", "MEDICACAO", "BUSCA_ENTREGA", "DESLOCAMENTO",
  "DURACAO_EXTRA", "KM", "ESPERA", "PARADA_ADICIONAL", "IDA_E_VOLTA",
  "PACOTE", "RECORRENCIA", "LONGA_DURACAO",
]);

const FRIENDLY_NAMES: Record<string, string> = {
  BANHO_ANTES_ENTREGA: "Banho antes da entrega",
  BANHO: "Banho",
  PASSEIO_INDIVIDUAL: "Passeio individual",
  PASSEIO_COMPARTILHADO: "Passeio compartilhado",
  VISITA_EXTRA: "Visita extra",
  PEDAGIO: "Pedágio",
  ESTACIONAMENTO: "Estacionamento",
  AVALIACAO_INICIAL: "Avaliação inicial",
  AULA_INDIVIDUAL: "Aula individual",
  AULA_GRUPO: "Aula em grupo",
  RETORNO: "Retorno",
  URGENCIA: "Urgência",
  MATERIAL: "Materiais",
  VACINACAO: "Vacinação",
  COLETA_EXAMES: "Coleta de exames",
  PORTE: "Porte do pet",
  PELAGEM: "Tipo de pelagem",
  HIDRATACAO: "Hidratação",
  DESEMBOLO: "Desembolo",
  CORTE_UNHAS: "Corte de unhas",
  TOSA_HIGIENICA: "Tosa higiênica",
};

function supports(service: GuidedPricingService, codes: string[]) {
  return codes.some((code) => service.allowed_modifiers.includes(code));
}

function money(value: number) {
  return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function percentLabel(value: number) {
  return `${Math.round(Number(value || 0) * 100)}%`;
}

function cappedFee(value: number, rate: number, cap: number) {
  const calculated = Math.max(0, Number(value || 0)) * Math.max(0, Number(rate || 0));
  return Number(cap || 0) > 0 ? Math.min(calculated, Number(cap)) : calculated;
}

function roundMoney(value: number) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function advertisedPriceForNet(netValue: number, rate: number, cap: number) {
  const desiredNet = Math.max(0, Number(netValue || 0));
  const normalizedRate = Math.min(0.999999, Math.max(0, Number(rate || 0)));
  if (normalizedRate === 0) return roundMoney(desiredNet);

  const uncappedAdvertised = desiredNet / (1 - normalizedRate);
  const uncappedFee = uncappedAdvertised * normalizedRate;
  if (Number(cap || 0) > 0 && uncappedFee > Number(cap)) {
    return roundMoney(desiredNet + Number(cap));
  }

  return roundMoney(uncappedAdvertised);
}

function unitLabel(unit: string) {
  return unit.replace(/^POR_/, "Por ").replaceAll("_", " ").toLowerCase();
}

function validityLabel(quantity: number, unit: PackageValidityUnit) {
  const amount = Math.max(1, Number(quantity || 1));
  const labels: Record<PackageValidityUnit, [string, string]> = {
    day: ["dia", "dias"],
    week: ["semana", "semanas"],
    month: ["mês", "meses"],
  };
  return `${amount} ${labels[unit][amount === 1 ? 0 : 1]}`;
}

function bathVariantForPriceKey(key: BathGroomingPriceKey) {
  if (key === "preco_banho") return "banho" as const;
  if (key === "preco_tosa") return "tosa" as const;
  return "banho_e_tosa" as const;
}

function codeLabel(code: string) {
  return FRIENDLY_NAMES[code] || code.replaceAll("_", " ").toLowerCase().replace(/^./, (letter) => letter.toUpperCase());
}

function QuestionCard({
  question,
  label,
  rule,
  onChange,
  children,
  showAmountFields = true,
}: {
  question: string;
  label: string;
  rule: Rule;
  onChange: (patch: Record<string, unknown>) => void;
  children?: React.ReactNode;
  showAmountFields?: boolean;
}) {
  const enabled = Boolean(rule.enabled);

  return (
    <div className={"rounded-2xl border p-4 transition " + (enabled ? "border-forest/40 bg-forest-light/40" : "bg-white")}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-medium">{question}</p>
          <p className="mt-1 text-xs text-muted-foreground">{enabled ? "Sim, este adicional será oferecido." : "Não, o preço normal será mantido."}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label={label}
          className={"relative h-7 w-12 shrink-0 rounded-full transition " + (enabled ? "bg-forest" : "bg-slate-300")}
          onClick={() => onChange({ enabled: !enabled })}
        >
          <span className={"absolute top-1 h-5 w-5 rounded-full bg-white shadow transition " + (enabled ? "left-6" : "left-1")} />
        </button>
      </div>
      {enabled && (
        <div className="mt-4 space-y-3">
          {showAmountFields && <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm font-medium">Como cobrar?
              <select
                className="mt-1 w-full rounded-lg border bg-white p-2.5"
                value={String(rule.type || "fixed")}
                onChange={(event) => onChange({ type: event.target.value })}
              >
                <option value="fixed">Valor em reais</option>
                <option value="percent">Percentual</option>
              </select>
            </label>
            <label className="text-sm font-medium">{rule.type === "percent" ? "Percentual" : "Valor"}
              <div className="mt-1 flex items-center rounded-lg border bg-white px-3">
                <span className="text-muted-foreground">{rule.type === "percent" ? "%" : "R$"}</span>
                <input
                  className="w-full bg-transparent p-2 outline-none"
                  type="number"
                  min="0"
                  step="0.01"
                  value={Number(rule.value || 0)}
                  onChange={(event) => onChange({ value: Number(event.target.value) })}
                />
              </div>
            </label>
          </div>}
          {children}
        </div>
      )}
    </div>
  );
}

export function HostPricingAssistant({
  service,
  saving,
  onPatchService,
  onPatchConfig,
  onSave,
  onShowAdvanced,
}: Props) {
  const config = service.pricing_config || {};
  const units = Array.from(new Set([service.pricing_unit, ...(service.alternative_units || [])].filter(Boolean)));
  const questions = useMemo(
    () => SIMPLE_QUESTIONS.filter((item) => supports(service, item.codes)),
    [service],
  );
  const specialCodes = useMemo(
    () => (service.allowed_modifiers || []).filter((code) => !CORE_CODES.has(code)),
    [service],
  );
  const activeSimple = questions.filter((item) => Boolean((config[item.key] as Rule | undefined)?.enabled));
  const activeSpecial = (config.extras || []).filter((extra) => extra.enabled && specialCodes.includes(extra.code));
  const hasPlans = supports(service, ["PACOTE", "RECORRENCIA", "LONGA_DURACAO"]);
  const packages = config.packages || [];
  const activeOperational = [config.distance, config.waiting, config.extra_stop, config.round_trip]
    .filter((rule) => Boolean(rule?.enabled)).length;
  const activePlans = (config.recurrence?.enabled ? 1 : 0)
    + (packages.length > 0 ? 1 : 0)
    + ((config.long_stay_discounts || []).length > 0 ? 1 : 0);
  const configuredCount = activeSimple.length + activeSpecial.length + activeOperational + activePlans;
  const isHosting = service.tipo_servico_id === 1;
  const isBathGrooming = service.tipo_servico_id === 8;
  const enabledBathOptions = BATH_GROOMING_OPTIONS.filter(({ key }) => service[key] !== null && service[key] !== undefined);
  const validBathPrices = enabledBathOptions
    .map(({ key }) => Number(service[key]))
    .filter((value) => Number.isFinite(value) && value > 0);
  const bathPricingIsValid = enabledBathOptions.length > 0 && validBathPrices.length === enabledBathOptions.length;
  const packageBasePrice = isBathGrooming
    ? Number(enabledBathOptions[0] ? service[enabledBathOptions[0].key] : 0)
    : Number(service.preco_base || 0);
  const firstLongStay = [...(config.long_stay_discounts || [])]
    .filter((rule) => Number(rule.discount_percent || 0) > 0)
    .sort((a, b) => Number(a.min_units) - Number(b.min_units))[0];
  const advertisedPrice = Math.max(0, Number(service.preco_base || 0));
  const policyFees = service.pricing_policy_fees;
  const configuredFeeRate = service.client_origin_default === "host"
    ? policyFees?.host_originated
    : policyFees?.marketplace_first;
  const feeRate = configuredFeeRate === undefined || !Number.isFinite(Number(configuredFeeRate))
    ? null
    : Math.max(0, Number(configuredFeeRate));
  const feeCap = Math.max(0, Number(policyFees?.maximum_amount || 0));
  const hostFeeAmount = feeRate === null ? null : roundMoney(cappedFee(advertisedPrice, feeRate, feeCap));
  const netPrice = hostFeeAmount === null ? advertisedPrice : roundMoney(Math.max(0, advertisedPrice - hostFeeAmount));
  const secondMarketplaceRate = Number(policyFees?.marketplace_second);
  const recurringMarketplaceRate = Number(policyFees?.marketplace_recurring);

  function patchDesiredNetPrice(rawValue: string) {
    const desiredNet = Math.max(0, Number(rawValue || 0));
    const nextAdvertisedPrice = feeRate === null
      ? roundMoney(desiredNet)
      : advertisedPriceForNet(desiredNet, feeRate, feeCap);
    onPatchService({ preco_base: nextAdvertisedPrice });
  }

  function patchPackages(next: PackageOption[]) {
    onPatchService({ pricing_config: { ...config, packages: next } });
  }

  function patchLongStay(minUnits: number, rawValue: string) {
    const rules = config.long_stay_discounts || [];
    const next = rules.filter((rule) => Number(rule.min_units) !== minUnits);
    if (rawValue !== "") {
      next.push({ min_units: minUnits, discount_percent: Number(rawValue) });
    }
    onPatchService({ pricing_config: { ...config, long_stay_discounts: next } });
  }

  function toggleSpecial(code: string) {
    const extras = [...(config.extras || [])];
    const index = extras.findIndex((extra) => extra.code === code);
    if (index >= 0) {
      extras[index] = { ...extras[index], enabled: !extras[index].enabled };
    } else {
      extras.push({
        id: "assistant-" + code.toLowerCase().replaceAll("_", "-"),
        code,
        name: codeLabel(code),
        enabled: true,
        type: "fixed",
        value: 0,
      });
    }
    onPatchService({ pricing_config: { ...config, extras } });
  }

  function patchSpecial(code: string, patch: Record<string, unknown>) {
    const extras = [...(config.extras || [])];
    const index = extras.findIndex((extra) => extra.code === code);
    if (index < 0) return;
    extras[index] = { ...extras[index], ...patch };
    onPatchService({ pricing_config: { ...config, extras } });
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-gradient-to-br from-forest-light/80 to-white p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-forest p-2 text-white"><Sparkles className="h-5 w-5" /></div>
          <div>
            <p className="font-semibold text-forest">Configuração rápida</p>
            <h3 className="mt-1 text-xl font-bold">Vamos definir o preço de {service.nome}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {isHosting
                ? "Só a diária é obrigatória. Descontos para estadias longas e adicionais podem ficar para depois."
                : "Só o preço é obrigatório. Adicionais, descontos e pacotes podem ficar para depois."}
            </p>
          </div>
        </div>
      </div>

      <section className="space-y-5">
        {isBathGrooming ? (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold">Quais serviços você oferece?</h3>
              <p className="text-sm text-muted-foreground">Marque apenas as modalidades que o tutor poderá reservar e informe o preço de cada uma.</p>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              {BATH_GROOMING_OPTIONS.map(({ key, label, help }) => {
                const enabled = service[key] !== null && service[key] !== undefined;
                return (
                  <div key={key} className={`rounded-2xl border p-4 transition ${enabled ? "border-forest/40 bg-forest-light/40" : "bg-white"}`}>
                    <label className="flex cursor-pointer items-start justify-between gap-3">
                      <span>
                        <span className="block font-semibold">{label}</span>
                        <span className="text-xs text-muted-foreground">{help}</span>
                      </span>
                      <input type="checkbox" className="mt-1 h-5 w-5 accent-forest" checked={enabled} onChange={(event) => onPatchService({ [key]: event.target.checked ? Number(service.preco_base || 0) : null })} />
                    </label>
                    {enabled && (
                      <label className="mt-4 block text-sm font-medium">Quanto você cobra?
                        <div className="mt-1 flex items-center rounded-xl border bg-white px-3">
                          <span className="text-muted-foreground">R$</span>
                          <input className="w-full bg-transparent p-3 text-lg font-semibold outline-none" type="number" min="0.01" step="0.01" value={service[key] ?? 0} onChange={(event) => onPatchService({ [key]: Number(event.target.value) })} />
                        </div>
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
            {enabledBathOptions.length === 0 && <p className="text-sm font-medium text-amber-700">Escolha pelo menos uma modalidade para publicar.</p>}
          </div>
        ) : (
          <>
            <div>
              <h3 className="text-lg font-semibold">Quanto você quer receber?</h3>
              <p className="text-sm text-muted-foreground">Informe o valor líquido desejado. A AllePets calcula automaticamente o valor que será cobrado no anúncio.</p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="text-sm font-medium">{isHosting ? "Minha diária líquida" : "Valor líquido que quero receber"}
                <div className="mt-1 flex items-center rounded-xl border bg-white px-3">
                  <span className="text-muted-foreground">R$</span>
                  <input className="w-full bg-transparent p-3 text-lg font-semibold outline-none" type="number" min="0" step="0.01" value={netPrice} onChange={(event) => patchDesiredNetPrice(event.target.value)} />
                </div>
              </label>
              <label className="text-sm font-medium">Esse valor é por
                <select className="mt-1 w-full rounded-xl border bg-white p-3" value={service.pricing_unit} onChange={(event) => onPatchService({ pricing_unit: event.target.value })}>
                  {units.map((unit) => <option key={unit} value={unit}>{unitLabel(unit)}</option>)}
                </select>
              </label>
            </div>

            <div className="rounded-2xl border border-forest/20 bg-forest-light/40 p-4 md:p-5">
              <div>
                <p className="font-semibold text-forest">Resumo do preço</p>
                <p className="mt-1 text-sm text-muted-foreground">O valor do anúncio é calculado para que você receba o líquido informado após a taxa aplicável à primeira reserva.</p>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <div className="rounded-xl border border-forest/20 bg-white p-4 shadow-sm">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Você recebe líquido</p>
                  <p className="mt-1 text-2xl font-bold text-forest">{money(netPrice)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{unitLabel(service.pricing_unit)}</p>
                </div>
                <div className="rounded-xl border border-forest/15 bg-white p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Valor cobrado no anúncio</p>
                  <p className="mt-1 text-xl font-bold text-forest">{money(advertisedPrice)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{unitLabel(service.pricing_unit)}</p>
                </div>
                <div className="rounded-xl border border-forest/15 bg-white p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Taxa total AllePets{feeRate !== null ? ` — ${percentLabel(feeRate)}` : ""}</p>
                  <p className="mt-1 text-xl font-bold">{hostFeeAmount === null ? "Indisponível" : money(hostFeeAmount)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">{service.client_origin_default === "host" ? "cliente trazido por você" : "1ª reserva com este tutor"}</p>
                </div>
              </div>
              {policyFees ? (
                <div className="mt-4 space-y-1 text-xs text-muted-foreground">
                  <p>
                    Você informa quanto quer receber. O tutor verá {money(advertisedPrice)} {unitLabel(service.pricing_unit)}. {service.client_origin_default === "host"
                      ? `Para cliente trazido por você, a taxa aplicada é ${feeRate !== null ? percentLabel(feeRate) : "a definida pela política vigente"}.`
                      : `Na 1ª reserva com este tutor, o cálculo preserva aproximadamente ${money(netPrice)} líquidos para você.`}
                  </p>
                  {service.client_origin_default !== "host" && Number.isFinite(secondMarketplaceRate) && Number.isFinite(recurringMarketplaceRate) && (
                    <p>Como o preço anunciado permanece o mesmo, seu líquido aumenta quando a taxa cai para {percentLabel(secondMarketplaceRate)} na 2ª reserva e {percentLabel(recurringMarketplaceRate)} da 3ª em diante.</p>
                  )}
                  {feeCap > 0 && <p>A comissão da AllePets tem teto de {money(feeCap)} por reserva, e esse teto também é considerado no cálculo do anúncio.</p>}
                </div>
              ) : (
                <p className="mt-4 text-xs text-muted-foreground">As taxas vigentes não puderam ser exibidas. Recarregue a página antes de publicar.</p>
              )}
            </div>
          </>
        )}

        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          Você informa quanto quer receber líquido. A AllePets calcula o valor do anúncio, aplica adicionais e descontos e recalcula a comissão no servidor antes da reserva.
        </div>
      </section>

      <details className="group rounded-2xl border bg-white">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 font-semibold">
          <span>Adicionais <span className="font-normal text-muted-foreground">(opcional)</span></span>
          <span className="text-sm font-normal text-forest">Configurar</span>
        </summary>
        <div className="border-t p-4 md:p-5">
          <section className="space-y-5">
            <div>
              <h3 className="text-lg font-semibold">O que pode alterar o preço?</h3>
              <p className="text-sm text-muted-foreground">Ative somente o que você realmente cobra. As outras opções permanecem desligadas.</p>
            </div>

            {questions.length > 0 && (
              <div className="grid gap-3 lg:grid-cols-2">
                {questions.map((item) => {
                  const rule = (config[item.key] as Rule | undefined) || {};
                  return (
                    <QuestionCard key={item.key} question={item.question} label={item.label} rule={rule} onChange={(patch) => onPatchConfig(item.key, patch)}>
                      {item.key === "extra_duration" && (
                        <label className="block text-sm font-medium">Cobrar a cada quantos minutos?
                          <input className="mt-1 w-full rounded-lg border bg-white p-2.5" type="number" min="1" value={Number(rule.unit_minutes || 60)} onChange={(event) => onPatchConfig(item.key, { unit_minutes: Number(event.target.value) })} />
                        </label>
                      )}
                    </QuestionCard>
                  );
                })}
              </div>
            )}

            {supports(service, ["KM", "DESLOCAMENTO"]) && (
              <QuestionCard question="Você cobra de acordo com a distância?" label="Distância" showAmountFields={false} rule={(config.distance || {}) as Rule} onChange={(patch) => onPatchConfig("distance", patch)}>
                <div className="grid gap-3 sm:grid-cols-3">
                  <label className="text-sm font-medium">Km incluídos<input className="mt-1 w-full rounded-lg border bg-white p-2.5" type="number" min="0" value={config.distance?.included_km ?? 0} onChange={(event) => onPatchConfig("distance", { included_km: Number(event.target.value) })} /></label>
                  <label className="text-sm font-medium">Preço por km<input className="mt-1 w-full rounded-lg border bg-white p-2.5" type="number" min="0" step="0.01" value={config.distance?.price_per_km ?? 0} onChange={(event) => onPatchConfig("distance", { price_per_km: Number(event.target.value) })} /></label>
                  <label className="text-sm font-medium">Cobrança mínima<input className="mt-1 w-full rounded-lg border bg-white p-2.5" type="number" min="0" step="0.01" value={config.distance?.minimum_fare ?? 0} onChange={(event) => onPatchConfig("distance", { minimum_fare: Number(event.target.value) })} /></label>
                </div>
              </QuestionCard>
            )}

            {supports(service, ["ESPERA"]) && (
              <QuestionCard question="Você cobra quando precisa esperar?" label="Tempo de espera" showAmountFields={false} rule={(config.waiting || {}) as Rule} onChange={(patch) => onPatchConfig("waiting", patch)}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="text-sm font-medium">Minutos sem cobrança<input className="mt-1 w-full rounded-lg border bg-white p-2.5" type="number" min="0" value={config.waiting?.free_minutes ?? 10} onChange={(event) => onPatchConfig("waiting", { free_minutes: Number(event.target.value) })} /></label>
                  <label className="text-sm font-medium">Preço por minuto excedente<input className="mt-1 w-full rounded-lg border bg-white p-2.5" type="number" min="0" step="0.01" value={config.waiting?.price_per_minute ?? 0} onChange={(event) => onPatchConfig("waiting", { price_per_minute: Number(event.target.value) })} /></label>
                </div>
              </QuestionCard>
            )}

            {supports(service, ["PARADA_ADICIONAL"]) && (
              <QuestionCard question="Você cobra por parada adicional?" label="Parada adicional" showAmountFields={false} rule={(config.extra_stop || {}) as Rule} onChange={(patch) => onPatchConfig("extra_stop", patch)}>
                <label className="block text-sm font-medium">Valor por parada<input className="mt-1 w-full rounded-lg border bg-white p-2.5" type="number" min="0" step="0.01" value={config.extra_stop?.value ?? 0} onChange={(event) => onPatchConfig("extra_stop", { value: Number(event.target.value) })} /></label>
              </QuestionCard>
            )}

            {supports(service, ["IDA_E_VOLTA"]) && (
              <QuestionCard question="Você cobra adicional para ida e volta?" label="Ida e volta" showAmountFields={false} rule={(config.round_trip || {}) as Rule} onChange={(patch) => onPatchConfig("round_trip", patch)}>
                <label className="block text-sm font-medium">Valor adicional<input className="mt-1 w-full rounded-lg border bg-white p-2.5" type="number" min="0" step="0.01" value={config.round_trip?.value ?? 0} onChange={(event) => onPatchConfig("round_trip", { value: Number(event.target.value) })} /></label>
              </QuestionCard>
            )}

            {specialCodes.length > 0 && (
              <div className="rounded-2xl border p-4">
                <p className="font-semibold">Outras opções de {service.nome.toLowerCase()}</p>
                <p className="mb-3 text-sm text-muted-foreground">Escolha somente as opções pelas quais você cobra à parte.</p>
                <div className="grid gap-3 md:grid-cols-2">
                  {specialCodes.map((code) => {
                    const extra = (config.extras || []).find((item) => item.code === code);
                    const enabled = Boolean(extra?.enabled);
                    return (
                      <div key={code} className={"rounded-xl border p-3 " + (enabled ? "border-forest/40 bg-forest-light/40" : "bg-white")}>
                        <label className="flex items-center justify-between gap-3 font-medium"><span>{codeLabel(code)}</span><input type="checkbox" checked={enabled} onChange={() => toggleSpecial(code)} /></label>
                        {enabled && extra && (
                          <div className="mt-3 grid grid-cols-2 gap-2">
                            <select className="rounded-lg border bg-white p-2 text-sm" value={extra.type} onChange={(event) => patchSpecial(code, { type: event.target.value })}><option value="fixed">Valor em reais</option><option value="percent">Percentual</option></select>
                            <input aria-label={"Valor de " + codeLabel(code)} className="rounded-lg border bg-white p-2 text-sm" type="number" min="0" step="0.01" value={extra.value} onChange={(event) => patchSpecial(code, { value: Number(event.target.value) })} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        </div>
      </details>

      <details className="group rounded-2xl border bg-white">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 p-4 font-semibold">
          <span>{isHosting ? "Descontos para estadias longas" : "Descontos e pacotes"} <span className="font-normal text-muted-foreground">(opcional)</span></span>
          <span className="text-sm font-normal text-forest">Configurar</span>
        </summary>
        <div className="border-t p-4 md:p-5">
          <section className="space-y-5">
            <div>
              <h3 className="text-lg font-semibold">{isHosting ? "Você oferece desconto para estadias longas?" : "Você oferece pacotes ou frequência?"}</h3>
              <p className="text-sm text-muted-foreground">{isHosting ? "O tutor continua reservando noites consecutivas. O desconto é aplicado automaticamente quando a estadia atinge a faixa configurada." : "Esta etapa é opcional. Você pode continuar sem configurar nenhum plano."}</p>
            </div>

            {!hasPlans && <div className="rounded-2xl border bg-slate-50 p-5 text-sm">Este serviço não precisa de pacotes ou recorrência. Pode seguir para a revisão.</div>}

            {supports(service, ["RECORRENCIA"]) && (
              <QuestionCard question="Você oferece desconto para clientes recorrentes?" label="Recorrência" showAmountFields={false} rule={{ enabled: config.recurrence?.enabled }} onChange={(patch) => onPatchConfig("recurrence", patch)}>
                <label className="block text-sm font-medium">Desconto oferecido<div className="mt-1 flex items-center rounded-lg border bg-white px-3"><span className="text-muted-foreground">%</span><input className="w-full bg-transparent p-2 outline-none" type="number" min="0" max="50" value={config.recurrence?.discount_percent ?? 0} onChange={(event) => onPatchConfig("recurrence", { discount_percent: Number(event.target.value) })} /></div></label>
              </QuestionCard>
            )}

            {supports(service, ["PACOTE"]) && (
              <div className="rounded-2xl border p-4">
                <div><p className="font-semibold">Pacotes pré-pagos</p><p className="text-sm text-muted-foreground">Defina quantos usos o tutor compra, o preço total e até quando poderá usar.</p></div>
                <div className="mt-4 space-y-3">
                  {packages.map((pkg, index) => (
                    <div key={pkg.id || index} className="rounded-xl bg-slate-50 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div><p className="font-semibold">Pacote com {Math.max(1, Number(pkg.units || 1))} utilizações</p><p className="text-xs text-muted-foreground">Válido por {validityLabel(pkg.validity_quantity || 1, pkg.validity_unit || "month")}</p></div>
                        <button type="button" className="text-red-600" aria-label="Remover pacote" onClick={() => patchPackages(packages.filter((_, itemIndex) => itemIndex !== index))}><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        {isBathGrooming && <label className="text-xs font-medium">Serviço do pacote<select className="mt-1 w-full rounded-lg border bg-white p-2 text-sm" value={pkg.variant_key || ""} onChange={(event) => { const next = [...packages]; next[index] = { ...pkg, variant_key: event.target.value as PackageOption["variant_key"] }; patchPackages(next); }}>{enabledBathOptions.map((option) => <option key={option.key} value={bathVariantForPriceKey(option.key)}>{option.label}</option>)}</select></label>}
                        <label className="text-xs font-medium">Quantidade de usos<input className="mt-1 w-full rounded-lg border bg-white p-2 text-sm" type="number" min="1" max="365" value={pkg.units} onChange={(event) => { const units = Number(event.target.value); const next = [...packages]; next[index] = { ...pkg, units, name: `Pacote com ${Math.max(1, units || 1)} utilizações` }; patchPackages(next); }} /></label>
                        <label className="text-xs font-medium">Preço total<input className="mt-1 w-full rounded-lg border bg-white p-2 text-sm" type="number" min="0" step="0.01" value={pkg.price} onChange={(event) => { const next = [...packages]; next[index] = { ...pkg, price: Number(event.target.value) }; patchPackages(next); }} /></label>
                        <div className="grid grid-cols-[90px_1fr] gap-2"><label className="text-xs font-medium">Validade<input className="mt-1 w-full rounded-lg border bg-white p-2 text-sm" type="number" min="1" max="365" value={pkg.validity_quantity || 1} onChange={(event) => { const next = [...packages]; next[index] = { ...pkg, validity_quantity: Number(event.target.value) }; patchPackages(next); }} /></label><label className="text-xs font-medium">Período<select className="mt-1 w-full rounded-lg border bg-white p-2 text-sm" value={pkg.validity_unit || "month"} onChange={(event) => { const next = [...packages]; next[index] = { ...pkg, validity_unit: event.target.value as PackageValidityUnit }; patchPackages(next); }}><option value="day">dia(s)</option><option value="week">semana(s)</option><option value="month">mês(es)</option></select></label></div>
                      </div>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => { const units = 4; const variantKey = isBathGrooming && enabledBathOptions[0] ? bathVariantForPriceKey(enabledBathOptions[0].key) : null; patchPackages([...packages, { name: `Pacote com ${units} utilizações`, units, price: Number((packageBasePrice * units * 0.9).toFixed(2)), validity_quantity: 1, validity_unit: "month", variant_key: variantKey }]); }}><Plus className="mr-2 h-4 w-4" />Criar pacote</Button>
              </div>
            )}

            {supports(service, ["LONGA_DURACAO"]) && (
              <div className="rounded-2xl border p-4">
                <p className="font-semibold">Desconto para estadias longas</p>
                <p className="text-sm text-muted-foreground">O desconto vale para todas as noites da reserva ao atingir a faixa. Deixe vazio quando não quiser oferecer desconto.</p>
                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  {[7, 15, 28].map((minUnits) => {
                    const found = (config.long_stay_discounts || []).find((rule) => Number(rule.min_units) === minUnits);
                    return (
                      <label className="text-sm font-medium" key={minUnits}>{minUnits} noites ou mais
                        <div className="mt-1 flex items-center rounded-lg border bg-white px-3"><span className="text-muted-foreground">%</span><input className="w-full bg-transparent p-2 outline-none" type="number" min="0" max="50" value={found?.discount_percent ?? ""} onChange={(event) => patchLongStay(minUnits, event.target.value)} /></div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        </div>
      </details>

      <div className="rounded-2xl border border-forest/20 bg-forest-light/50 p-4">
        <div className="flex gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-forest" />
          <div className="min-w-0 text-sm">
            <p className="font-semibold">O que será publicado</p>
            {isBathGrooming ? (
              <>
                <p className="mt-1 text-muted-foreground">Na busca, o tutor verá cada serviço com seu próprio preço:</p>
                <div className="mt-3 grid gap-2 sm:grid-cols-3">{enabledBathOptions.map(({ key, label }) => <div key={key} className="flex items-center justify-between gap-3 rounded-xl border border-forest/15 bg-white px-3 py-2"><span className="font-medium">{label}</span><strong className="whitespace-nowrap text-forest">{money(Number(service[key]))}</strong></div>)}</div>
                <p className="mt-2 text-xs text-muted-foreground">O tutor escolherá um desses serviços antes de reservar. Não será divulgado um preço único.</p>
              </>
            ) : (
              <p className="mt-1 text-lg font-bold">{money(service.preco_base)} <span className="text-sm font-normal text-muted-foreground">{isHosting ? "por noite" : unitLabel(service.pricing_unit)}</span></p>
            )}
            {isHosting && firstLongStay && <p className="mt-2 font-medium text-forest">Desconto de {Number(firstLongStay.discount_percent)}% para estadias de {Number(firstLongStay.min_units)} noites ou mais.</p>}
            <p className="mt-2 text-muted-foreground">{configuredCount} configuração(ões) opcional(is). A AllePets recalcula tudo no servidor e preserva o valor das reservas já criadas.</p>
          </div>
        </div>
      </div>

      <Button type="button" className="w-full py-6 text-base md:w-auto" onClick={onSave} disabled={saving || (isBathGrooming ? !bathPricingIsValid : service.preco_base < 0)}><CheckCircle2 className="mr-2 h-5 w-5" />{saving ? "Salvando..." : "Salvar e publicar na busca"}</Button>
      <p className="text-xs text-muted-foreground">Depois de salvar, somente as modalidades marcadas aparecem na busca e podem ser reservadas.</p>

      <div className="border-t pt-3"><Button type="button" variant="ghost" size="sm" onClick={onShowAdvanced}><Settings2 className="mr-2 h-4 w-4" />Preciso de mais opções</Button></div>
    </div>
  );
}
